import { GridWidgetTask } from './grid-widget-task';
import { GridWidgetDataField } from './grid-widget-data-field';
import { SortConfiguration } from './sort-configuration';
import { OrderedMap } from '../ordered-map';
import { PdsNaturalSort } from '../../pds-natural-sort';

let naturalSort = new PdsNaturalSort;
naturalSort.ignoreCase();

export class SortTask extends GridWidgetTask {

  /**
   * The name of the task. Something like 'Filter', 'Paginate', or 'Sort'
   */
  name = 'sort';

  sortConfiguration: SortConfiguration | null | undefined;

  fieldList: OrderedMap<GridWidgetDataField>;

  //These were added in so that the sort task could be properly restored.
  sortConfigurations: Array<SortConfiguration> | undefined;

  /**
   * The results of the last run will be stored here.
   */
  result: Array<any>;

  constructor(dataFields: OrderedMap<GridWidgetDataField>) {
    super();
    this.fieldList = dataFields;
  }
  getPriority(): number {
    return GridWidgetTask.SORT_TASK_PRIORITY;
  }

  preRunSetup(): void {
    if (!this.isGridWidgetTaskActive()) {
      if (this.sortConfiguration != null && this.sortConfiguration.reversed != null) {
        this.restArguments = this.sortConfiguration.getRestArguments();
      } else {
        this.restArguments = [];
      }
    }
  }

  /**
   * The main function to exectute.
   */
  run(dataRows: Array<any>): Array<any> {
    if (this.sortConfiguration == null) {
      return dataRows;
    }
    let naturalSortConfig: Array<{ sortDirection?: string, extractionKey: ((rowData: any) => any) | string}> = [];
    for (let fieldId of this.sortConfiguration.fields) {
      // fieldConfiguration has a reference to the sort direction, but we need to override the extraction key.
      // because the current extraction key is just a field id.
      let fieldConfiguration = naturalSort.splitConfigurationSetting(fieldId);
      let dataField = this.fieldList.get(fieldConfiguration.extractionKey);
      if (dataField != null) {
        fieldConfiguration.extractionKey = dataField.resolve.bind(dataField);
        naturalSortConfig.push(fieldConfiguration);
      }
    }
    let sortedData = naturalSort.sortObjectsAdvanced(dataRows, naturalSortConfig, this.sortConfiguration.reversed || undefined);
    return sortedData;
  }

  /**
   * The sort task needs to know what the currently available Data Fields are, the fields to attempt to sort on, and
   * the direction to sort everything.
   * @param sortConfiguration
   * @param fieldList
   */
  update(sortConfiguration: SortConfiguration | undefined | null, fieldList: OrderedMap<GridWidgetDataField>): void {
    if (this.sortConfiguration !== sortConfiguration) {
      //There is a new sort configuration.
      if (this.sortConfiguration != null) {
        //There is already one there. Nuke the sort direction and run its callback.
        this.sortConfiguration.reversed = null;
        if (this.sortConfiguration.sortChangedCallback != null) {
          this.sortConfiguration.sortChangedCallback();
        }
      }

      this.sortConfiguration = sortConfiguration;

      if (this.sortConfiguration != null && this.sortConfiguration.sortChangedCallback != null) {
        this.sortConfiguration.sortChangedCallback();
      }
    }

    this.fieldList = fieldList;
  }

  getPersistenceObject(): Object | undefined {
    if (this.sortConfiguration) {
      return this.sortConfiguration.getPersistenceObject();
    }
  }

  restoreFromPersistenceObject(value: Object | undefined): void {
    let state = <{fields: Array<string>, reversed?: boolean}>value;
    if (state && state.fields instanceof Array && state.fields.length > 0) {
      //At this point we need to restore something.
      if (this.sortConfigurations == null) {
        //So something tried to restore, and sortConfigurations weren't passed in so we log an error.
        console.error('Sort Configurations not available to restore the state of the sort task.');
        delete this.sortConfiguration;
      } else {
        this.sortConfiguration = this.sortConfigurations.find((sortConfig: SortConfiguration) => {
          return sortConfig.fields.join('|') === state.fields.join('|');
        });
      }

      if (this.sortConfiguration != null) {
        //If nothing was found, we just clear the sort configuration so no sorting will occur until someone clicks
        //something to turn sorting back on.
        this.sortConfiguration.reversed = state.reversed;
      }
    } else {
      delete this.sortConfiguration;
    }
  }
}
